import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ConsigneeSettlement, Sale } from '../lib/database.types'
import { SALES_KEY } from './useSales'

export const SETTLEMENTS_KEY = ['consignee_settlements']

export function useConsigneeSettlements(consigneeId: string | undefined) {
  return useQuery({
    queryKey: [...SETTLEMENTS_KEY, consigneeId],
    enabled: !!consigneeId,
    queryFn: async (): Promise<ConsigneeSettlement[]> => {
      const { data, error } = await supabase
        .from('consignee_settlements')
        .select('*')
        .eq('consignee_id', consigneeId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

/** Pure helper: total of mom's UNSETTLED non-refunded sales for one consignee. */
export interface UnsettledTotals {
  count: number
  totalSold: number
  momCut: number
  ownerReceives: number
}

export function computeUnsettledTotals(sales: Sale[], consigneeId: string): UnsettledTotals {
  let count = 0
  let totalSold = 0
  let momCut = 0
  for (const s of sales) {
    if (s.consignee_id !== consigneeId) continue
    if (s.settlement_id != null) continue
    if (s.is_refunded) continue
    count += 1
    totalSold += Number(s.gross_amount)
    momCut += Number(s.moms_cut_at_sale ?? 0)
  }
  return {
    count,
    totalSold,
    momCut,
    ownerReceives: totalSold - momCut,
  }
}

export function useSettleConsignee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { consignee_id: string; note?: string | null }): Promise<string> => {
      const { data, error } = await supabase.rpc('settle_consignee', {
        p_consignee_id: input.consignee_id,
        p_note: input.note ?? null,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY })
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
    },
  })
}
